import { useState, type FormEvent } from 'react'
import { ShieldCheck } from 'lucide-react'
import { listTotpFactors, verifyTotpCode } from '@/auth/mfa'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MfaCodeInput } from '@/features/mfa/MfaCodeInput'

/**
 * Login step-up challenge (spec-14b Phase 2). Shown after a successful
 * password sign-in when the session is `aal1` but a verified factor exists
 * (`nextLevel === 'aal2'`). Prompts for the TOTP code and elevates the session
 * to aal2 via challenge + verify *before* the app is entered.
 *
 * Cancelling does not hard-fail the session — the user stays aal1; the
 * StaffMfaGate then blocks the staff shell until they step up or log out.
 */
export function MfaStepUpDialog({
  open,
  onElevated,
  onCancel,
}: {
  open: boolean
  onElevated: () => void
  onCancel: () => void
}) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      // Verify against the first verified TOTP factor — v1 enrols exactly one.
      const factors = await listTotpFactors()
      const verified = factors.find((factor) => factor.status === 'verified')
      if (!verified) {
        // No verified factor after all: nothing to step up to, let the user in.
        onElevated()
        return
      }
      await verifyTotpCode(verified.id, code)
      setCode('')
      onElevated()
    } catch {
      setError('Onjuiste of verlopen code. Probeer het opnieuw.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onCancel() : undefined)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-primary" aria-hidden />
            Tweestapsverificatie
          </DialogTitle>
          <DialogDescription>
            Voer de 6-cijferige code uit je authenticator-app in om in te loggen.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
          <MfaCodeInput
            id="mfa-stepup-code"
            value={code}
            onChange={setCode}
            disabled={submitting}
            autoFocus
          />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button
            type="submit"
            size="lg"
            disabled={submitting || code.length !== 6}
            className="w-full text-base"
          >
            {submitting ? 'Bezig met verifiëren…' : 'Verifiëren'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Annuleren
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
